import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubjects } from '../lib/api';
import { SUBJECTS } from '../lib/utils';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Plane, FileQuestion, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const DashboardPage = () => {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadSubjects();
  }, []);

  const loadSubjects = async () => {
    try {
      const response = await getSubjects();
      setSubjects(response.data);
    } catch (err) {
      toast.error('Erro ao carregar matérias');
    } finally {
      setLoading(false);
    }
  };

  const handleStartQuiz = (subjectId) => {
    const subject = subjects.find(s => s.id === subjectId);
    if (subject && subject.question_count < 1) {
      toast.error('Não há questões disponíveis para esta matéria');
      return;
    }
    navigate(`/quiz/${subjectId}`);
  };

  const subjectIcons = {
    teoria_de_voo: '✈️',
    conhecimentos_tecnicos: '🔧',
    meteorologia: '🌤️',
    navegacao_aerea: '🧭',
    regulamentos: '📋',
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto animate-fadeIn">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <img src="/logo-voar.png" alt="VOAR" className="h-12" />
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-zinc-900">
                Simulados V.O.A.R.
              </h1>
              <p className="text-zinc-500">
                Selecione uma matéria para iniciar seu simulado
              </p>
            </div>
          </div>
        </div>

        {/* Subject Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white border border-zinc-200 rounded-xl overflow-hidden animate-pulse">
                <div className="h-40 bg-zinc-200" />
                <div className="p-6">
                  <div className="h-6 bg-zinc-200 rounded mb-2" />
                  <div className="h-4 bg-zinc-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subjects.map((subject, index) => {
              const subjectInfo = SUBJECTS[subject.id];
              return (
                <div
                  key={subject.id}
                  className="subject-card group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Image */}
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={subjectInfo?.image}
                      alt={subject.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <span className="text-3xl">{subjectIcons[subject.id]}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-zinc-900 mb-2 line-clamp-2">
                      {subject.name}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-zinc-500 mb-4">
                      <FileQuestion className="w-4 h-4" />
                      <span>{subject.question_count} questões disponíveis</span>
                    </div>
                    <Button
                      onClick={() => handleStartQuiz(subject.id)}
                      className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg btn-press group"
                      disabled={subject.question_count < 1}
                      data-testid={`start-quiz-${subject.id}`}
                    >
                      <span>Iniciar Simulado</span>
                      <ChevronRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info Card */}
        <div className="mt-12 bg-white border border-zinc-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-zinc-900 mb-2">
            Sobre o Simulado
          </h3>
          <ul className="space-y-2 text-zinc-600">
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-1">•</span>
              <span>Cada simulado contém até 20 questões aleatórias da matéria escolhida</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-1">•</span>
              <span>Não há limite de tempo - responda no seu ritmo</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-1">•</span>
              <span>Ao finalizar, você verá o resultado detalhado com as respostas corretas</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-1">•</span>
              <span>Seu histórico e estatísticas ficam salvos para acompanhar sua evolução</span>
            </li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
